import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import { createBlogPost } from '../graphql/mutations';
import { getCurrentUser } from 'aws-amplify/auth';
import { FileText, Image, User, Hash } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './BlogPostForm.css';

const BlogPostForm = () => {
  const [formState, setFormState] = useState({
    title: '',
    content: '',
    excerpt: '',
    author: '',
    svgContent: '',
    iconName: ''
  });
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const client = generateClient();

  useEffect(() => {
    checkUser();
  }, []);

  async function checkUser() {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      setFormState(prevState => ({
        ...prevState,
        author: currentUser.username
      }));
    } catch (err) {
      console.error('Error fetching user:', err);
      setError('Unable to authenticate user. Please try signing in again.');
    } finally {
      setIsLoading(false);
    }
  }

  function setInput(key, value) {
    setFormState({ ...formState, [key]: value });
  }

  async function addBlogPost(e) {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    try {
      if (!formState.title || !formState.content) {
        setError('Title and content are required.');
        return;
      }
      const blogPost = { 
        ...formState, 
        publishDate: new Date().toISOString(),
        author: user.username
      };
      const response = await client.graphql({ 
        query: createBlogPost, 
        variables: { input: blogPost } 
      });
      console.log('Blog post created successfully:', response.data.createBlogPost);
      setSuccess(true);
      setTimeout(() => {
        navigate('/blog');
      }, 2000); // Redirect after 2 seconds
    } catch (err) {
      console.error('Error creating blog post:', err);
      setError('Failed to create blog post. Please try again.');
    }
  }

  if (isLoading) {
    return <p>Loading...</p>;
  }

  if (error) {
    return <p className="error-message">{error}</p>;
  }

  if (!user) {
    return <p>Please sign in to create a blog post.</p>;
  }

  return (
    <form className="blog-post-form" onSubmit={addBlogPost}>
      <h2 className="form-title">Add New Blog Post</h2>
      
      {success && <p className="success-message">Blog post created successfully! Redirecting to blog list...</p>}
      
      <div className="form-group">
        <label htmlFor="title" className="form-label">
          <FileText size={18} />
          Title
        </label>
        <input
          id="title"
          className="form-input"
          onChange={event => setInput('title', event.target.value)}
          value={formState.title}
          placeholder="Enter post title"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="author" className="form-label">
          <User size={18} />
          Author
        </label>
        <input
          id="author"
          className="form-input"
          value={user.username}
          readOnly
        />
      </div>

      <div className="form-group">
        <label htmlFor="excerpt" className="form-label">
          <FileText size={18} />
          Excerpt
        </label>
        <textarea
          id="excerpt"
          className="form-textarea"
          onChange={event => setInput('excerpt', event.target.value)}
          value={formState.excerpt}
          placeholder="Enter a brief excerpt"
          rows="3"
        />
      </div>

      <div className="form-group">
        <label htmlFor="content" className="form-label">
            <FileText size={18} />
            Content
        </label>
        <textarea
            id="content"
            className="form-textarea"
            onChange={event => setInput('content', event.target.value)}
            value={formState.content}
            placeholder="Enter post content (Markdown supported)"
            rows="10"
            required
        />
        <small className="form-help-text">
            Markdown is supported. Use # for headers, * for lists, ** for bold, etc.
        </small>
        </div>

      <div className="form-group">
        <label htmlFor="svgContent" className="form-label">
          <Image size={18} />
          SVG Content
        </label>
        <textarea
          id="svgContent"
          className="form-textarea"
          onChange={event => setInput('svgContent', event.target.value)}
          value={formState.svgContent}
          placeholder="Enter SVG content"
          rows="5"
        />
      </div>

      <div className="form-group">
        <label htmlFor="iconName" className="form-label">
          <Hash size={18} />
          Icon Name
        </label>
        <input
          id="iconName"
          className="form-input"
          onChange={event => setInput('iconName', event.target.value)}
          value={formState.iconName}
          placeholder="Enter icon name"
        />
      </div>

      <button type="submit" className="submit-button">Create Blog Post</button>
    </form>
  );
};

export default BlogPostForm;